/**
 * related-products-fish.js — client-side enhancement of the Related Products block.
 *
 * Fetches the fish recommendations endpoint for the current product and renders
 * ranked-by-relevance cards into the existing `.relatedProducts` grid.
 *
 * Card markup mirrors snippets/product-card.liquid (.bestSellingCard) WITHOUT the
 * `.bestSellingColors` swatch row — Related shows one card per product, single image.
 *
 * Fallback contract (per BE-FISH-RELATED-PRODUCTS.md):
 *   - 200 + hits[]      -> replace grid with fish cards, show section
 *   - 200 + hits: []    -> keep server Liquid cards; hide only if none existed
 *   - 404               -> hide the block
 *   - 5xx/timeout/error -> keep server Liquid fallback (do nothing)
 *
 * Endpoint: GET https://fish.limm.space/api/products/{handle}/related
 *           ?shop={domain}&limit={n}&strict_type={bool}
 *           [&frame_color={name}&lens_color={name}]
 * Multi-tenant: `shop` is always sent (see CLAUDE.md multi-tenant note).
 *
 * Colour-aware: on the product page we read the selected Frame/Lens colour from
 * #variants and re-fetch (debounced) whenever the shopper switches colour, so the
 * block re-ranks around the active colour. frame_color/lens_color are ignored by
 * fish until the backend ships colour ranking (see BE-FISH-RELATED-COLOR-PARAM.md).
 */
(function () {
  'use strict';

  var API_BASE = 'https://fish.limm.space/api/products/';
  var TIMEOUT_MS = 5000;

  var WISHLIST_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">' +
    '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>' +
    '</svg>';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Escape a string that will live inside single quotes of an inline JS handler.
  function jsStr(s) {
    return String(s == null ? '' : s)
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
      .replace(/</g, '\\x3C');
  }

  // Append a width param to a Shopify CDN url (which may already carry ?v=...).
  function withWidth(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }

  // Derive a currency symbol from a pre-formatted price string ("From $70.00" -> "$").
  function currencySymbol(sample) {
    var m = sample && sample.match(/[^\d.,\s]+/);
    return m ? m[0] : '$';
  }

  // Strip a leading "From " prefix, keeping the amount ("From $70.00" -> "$70.00").
  function amountOnly(priceFormatted) {
    return String(priceFormatted || '').replace(/^\s*From\s+/i, '');
  }

  function priceBlock(hit) {
    var price = typeof hit.price === 'number' ? hit.price : parseFloat(hit.price) || 0;
    var cmp = hit.compare_at_price == null ? null : parseFloat(hit.compare_at_price);
    var amount = amountOnly(hit.price_formatted) || currencySymbol(hit.price_formatted) + price.toFixed(2);

    if (cmp != null && cmp > price && price > 0) {
      var pct = Math.round(((cmp - price) / cmp) * 100);
      var original = currencySymbol(hit.price_formatted) + cmp.toFixed(2);
      return (
        '<div class="salePriceWrapper">' +
        '<div class="salePrices">From <span class="salePrice">' + esc(amount) + '</span> ' +
        '<span class="originalPrice">' + esc(original) + '</span></div>' +
        '<span class="saleBadge">SALE ' + pct + '% OFF</span>' +
        '</div>'
      );
    }
    return '<div class="bestSellingPrice">From <strong>' + esc(amount) + '</strong></div>';
  }

  // Append " | Size X, Y" when the product has multiple sizes (single-size products
  // already carry it baked into hit.title). Sizes come from "Size:NN" tags — this
  // endpoint doesn't expose a dedicated option_size field (see BE-FISH-RELATED-PRODUCTS.md).
  function displayTitle(hit) {
    var title = hit.title || '';
    if (title.indexOf('Size') !== -1 || !Array.isArray(hit.tags)) return title;
    var sizes = hit.tags
      .filter(function (t) { return t.indexOf('Size:') === 0; })
      .map(function (t) { return t.slice(5); });
    if (sizes.length > 1) title += ' | Size ' + sizes.join(', ');
    return title;
  }

  function cardHTML(hit, cfg) {
    var url = hit.url || '/products/' + hit.handle;
    var title = hit.title || '';
    var img = withWidth(hit.image, 400);
    var hover = hit.image_hover ? withWidth(hit.image_hover, 400) : '';

    var icons = '<div class="bestSellingIconsWrapper">' +
      '<button class="bestSellingLikeIcon" aria-label="Add to wishlist"' +
      ' data-product-id="' + esc(hit.id) + '"' +
      ' data-product-url="' + esc(url) + '"' +
      ' data-product-title="' + esc(title) + '"' +
      ' data-product-image="' + esc(withWidth(hit.image, 160)) + '"' +
      ' data-product-price="' + esc(hit.price_formatted || '') + '"' +
      ' onclick="event.stopPropagation();">' + WISHLIST_SVG + '</button>';
    if (hit.is_rx_available && cfg.rxIcon) {
      icons += '<div class="bestSellingBrandIcon">' +
        '<img src="' + esc(cfg.rxIcon) + '" alt="RX Available" loading="lazy" width="30" height="30"></div>';
    }
    icons += '</div>';

    var kids = '';
    if (hit.is_kids && cfg.kidsDesktop) {
      kids = '<div class="kidsSizeIcon">' +
        '<img src="' + esc(cfg.kidsDesktop) + '" alt="Kids Size" class="kidsSizeDesktop" width="120" height="60" loading="lazy">' +
        '<img src="' + esc(cfg.kidsMobile) + '" alt="Kids Size" class="kidsSizeMobile" width="80" height="40" loading="lazy">' +
        '</div>';
    }

    var imgs = '';
    if (img) {
      imgs += '<img src="' + esc(img) + '" alt="' + esc(title) + '"' +
        ' class="bestSellingImage bestSellingImageDefault" loading="lazy" width="400" height="400">';
    }
    if (hover) {
      imgs += '<img src="' + esc(hover) + '" alt="' + esc(title) + '"' +
        ' class="bestSellingImage bestSellingImageHover" loading="lazy" width="400" height="400">';
    }

    // matched_color — optional QA hint from fish when the card image was picked to
    // match the shopper's selected Frame colour. Themed code ignores it; exposed as
    // a data-attr for debugging/analytics only.
    var matched = hit.matched_color ? ' data-matched-color="' + esc(hit.matched_color) + '"' : '';

    return (
      '<div class="bestSellingCard" tabindex="0" role="link" aria-label="' + esc(title) + '"' + matched +
      " onclick=\"window.location.href='" + jsStr(url) + "'\"" +
      " onkeydown=\"if(event.key==='Enter'||event.key===' '){event.preventDefault();window.location.href='" + jsStr(url) + "';}\">" +
      '<div class="bestSellingImageBox">' + icons + kids +
      '<a href="' + esc(url) + '">' + imgs + '</a>' +
      '</div>' +
      '<div class="bestSellingInfo">' +
      '<div class="bestSellingInfoTop">' +
      '<div class="bestSellingName"><a href="' + esc(url) + '">' + esc(displayTitle(hit)) + '</a></div>' +
      priceBlock(hit) +
      '</div>' +
      '<a href="' + esc(url) + '" class="bestSellingBtn" aria-hidden="true" tabindex="-1">' +
      esc(cfg.viewDetails || 'VIEW DETAILS') + '</a>' +
      '</div>' +
      '</div>'
    );
  }

  // Read the currently selected Frame/Lens colour from the product's #variants block.
  // Returns {frame, lens} (either may be ''), so Related can re-rank by the active colour.
  function readSelectedColors() {
    var out = { frame: '', lens: '' };
    var vars = document.getElementById('variants');
    if (!vars) return out;
    var selects = vars.querySelectorAll('select.variantSwatchSelect');
    for (var i = 0; i < selects.length; i++) {
      var name = (selects[i].getAttribute('name') || '').toLowerCase();
      var val = selects[i].value || '';
      if (name.indexOf('frame color') >= 0) out.frame = val;
      else if (name.indexOf('lens color') >= 0) out.lens = val;
    }
    return out;
  }

  function enhance(section) {
    if (section.dataset.relatedInit === '1') return;
    section.dataset.relatedInit = '1';

    var handle = section.dataset.relatedHandle;
    if (!handle) return;

    var shop = section.dataset.shop || (window.Shopify && window.Shopify.shop) || '';
    var grid = section.querySelector('.relatedProductsGrid');
    if (!grid) return;

    var cfg = {
      rxIcon: section.dataset.rxIcon,
      kidsDesktop: section.dataset.kidsDesktop,
      kidsMobile: section.dataset.kidsMobile,
      viewDetails: section.dataset.viewDetails
    };

    var hasServerCards = !!grid.querySelector('.bestSellingCard');
    var limit = parseInt(section.dataset.relatedLimit, 10) || 10;
    var strict = section.dataset.relatedStrict !== 'false';

    var baseUrl = API_BASE + encodeURIComponent(handle) + '/related' +
      '?shop=' + encodeURIComponent(shop) +
      '&limit=' + limit +
      '&strict_type=' + (strict ? 'true' : 'false');

    var activeCtrl = null;

    // Fetch + render Related for the given colour selection. `colors` is
    // {frame, lens}; empty values are omitted. Unknown query params are ignored
    // by fish until the backend ships colour-aware ranking (graceful degrade).
    function doFetch(colors) {
      var url = baseUrl;
      if (colors && colors.frame) url += '&frame_color=' + encodeURIComponent(colors.frame);
      if (colors && colors.lens) url += '&lens_color=' + encodeURIComponent(colors.lens);

      if (activeCtrl) { try { activeCtrl.abort(); } catch (e) {} }
      var ctrl = new AbortController();
      activeCtrl = ctrl;
      var timer = setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS);

      fetch(url, { signal: ctrl.signal, headers: { Accept: 'application/json' } })
        .then(function (res) {
          clearTimeout(timer);
          if (res.status === 404) {
            section.hidden = true;
            return null; // handled
          }
          if (!res.ok) throw new Error('http ' + res.status); // 5xx -> fallback
          return res.json();
        })
        .then(function (data) {
          if (!data) return;
          if (ctrl !== activeCtrl) return; // superseded by a newer colour change
          var hits = (data && data.hits) || [];
          if (!hits.length) {
            if (!hasServerCards) section.hidden = true;
            return;
          }
          var html = '';
          for (var i = 0; i < hits.length; i++) html += cardHTML(hits[i], cfg);
          grid.innerHTML = html;
          section.hidden = false;
          // Re-apply wishlist liked-state to freshly injected hearts.
          if (typeof window.hgInitWishlistButtons === 'function') {
            try { window.hgInitWishlistButtons(); } catch (e) {}
          }
        })
        .catch(function (err) {
          clearTimeout(timer);
          if (err && err.name === 'AbortError') return; // superseded / timeout
          // network / 5xx -> keep server fallback (cards stay, or section stays
          // hidden if there were none). Nothing to do.
          if (window.console) console.warn('related fish unavailable, keeping fallback', err);
        });
    }

    // Initial load — colour-aware from the current variant selection. Deferred
    // to `load` (18.09.2026, per fish backend dev) — the fetch is now
    // edge-cached (~270ms after their Cloudflare fix) but still shouldn't
    // compete with the critical rendering path. Server Liquid fallback cards
    // stay visible (per the fallback contract above) until this resolves.
    var initColors = readSelectedColors();
    var fireInitialFetch = function () { doFetch(initColors); };
    if (document.readyState === 'complete') {
      fireInitialFetch();
    } else {
      window.addEventListener('load', fireInitialFetch, { once: true });
    }

    // Re-fetch when the shopper switches Frame/Lens colour in #variants.
    var variants = document.getElementById('variants');
    if (variants) {
      var deb = null;
      var lastKey = initColors.frame + '|' + initColors.lens;
      var schedule = function () {
        var c = readSelectedColors();
        var key = c.frame + '|' + c.lens;
        if (key === lastKey) return; // colour unchanged -> skip
        lastKey = key;
        clearTimeout(deb);
        deb = setTimeout(function () { doFetch(c); }, 300);
      };
      variants.addEventListener('change', function (e) {
        var t = e.target;
        if (t && t.classList && t.classList.contains('variantSwatchSelect')) schedule();
      });
      // Swatch <li> clicks update the <select> programmatically (no change event);
      // read after the theme has applied the new value.
      variants.addEventListener('click', function (e) {
        var li = e.target && e.target.closest ? e.target.closest('.variantSwatchItem') : null;
        if (li) setTimeout(schedule, 0);
      });
    }
  }

  function init() {
    var sections = document.querySelectorAll('.relatedProducts[data-related-handle]');
    for (var i = 0; i < sections.length; i++) enhance(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
